import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import {
    Search,
    UserPlus,
    User,
    Shield,
    Trash2,
    RefreshCw,
    X,
    Mail,
    UserCheck,
    Check
} from 'lucide-react';
import userService from '../../services/userService';
import Toast from '../common/Toast';
import { useUser } from '../../context/UserContext';

const AdminUsers = () => {
    const { user: currentUser } = useUser();
    const [searchTerm, setSearchTerm] = useState('');
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [toast, setToast] = useState(null);

    // Modal states
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // stores user id to delete

    // Form state
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        password: '',
        confirmPassword: '',
        is_staff: true,
        is_active: true
    });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        setIsLoading(true);
        try {
            const data = await userService.getAdminUsers();
            setUsers(data);
        } catch (error) {
            console.error('Error loading administrators:', error);
            setToast({ message: 'Erreur lors du chargement des administrateurs', type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.full_name.trim()) errors.full_name = 'Le nom est requis';
        if (!formData.email.trim()) {
            errors.email = "L'email est requis";
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
            errors.email = "Format d'email invalide";
        }
        if (!formData.password) {
            errors.password = "Le mot de passe est requis";
        } else if (formData.password.length < 6) {
            errors.password = "Le mot de passe doit contenir au moins 6 caractères";
        }
        if (formData.password !== formData.confirmPassword) {
            errors.confirmPassword = "Les mots de passe ne correspondent pas";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error when typing
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            await userService.createAdminUser(formData);
            setToast({ message: 'Administrateur créé avec succès', type: 'success' });
            setIsAddUserModalOpen(false);
            setFormData({
                full_name: '',
                email: '',
                password: '',
                confirmPassword: '',
                is_staff: true,
                is_active: true
            });
            loadUsers();
        } catch (error) {
            console.error('Error creating admin:', error);
            const errorMsg = error.response?.data?.email?.[0] || 'Erreur lors de la création de l\'administrateur';
            setToast({ message: errorMsg, type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        try {
            await userService.deleteAdminUser(userId);
            setToast({ message: 'Administrateur supprimé avec succès', type: 'success' });
            setShowDeleteConfirm(null);
            loadUsers();
        } catch (error) {
            console.error('Error deleting admin:', error);
            setToast({ message: 'Erreur lors de la suppression', type: 'error' });
        }
    };

    const filteredUsers = users.filter(usr => {
        const searchLower = searchTerm.toLowerCase();
        return usr.full_name?.toLowerCase().includes(searchLower) ||
            usr.email?.toLowerCase().includes(searchLower);
    });

    return (
        <div className="space-y-10">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-black text-green-950 mb-2">Administrateurs</h1>
                    <p className="text-slate-500">Gérez les accès et les permissions de votre équipe.</p>
                </div>
                <div className="flex gap-3 w-full sm:w-auto">
                    <Button variant="secondary" onClick={loadUsers} className="h-12 px-5">
                        <RefreshCw className="w-5 h-5" />
                    </Button>
                    {currentUser?.is_superuser && (
                        <Button
                            variant="primary"
                            onClick={() => setIsAddUserModalOpen(true)}
                            className="h-12 px-6 flex items-center gap-2 w-full sm:w-auto justify-center"
                        >
                            <UserPlus className="w-5 h-5" />
                            Ajouter un admin
                        </Button>
                    )}
                </div>
            </div>

            {/* List & Search */}
            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden flex flex-col">
                <div className="p-6 border-b border-slate-100 flex items-center gap-4">
                    <div className="relative flex-grow">
                        <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                        <input
                            type="text"
                            placeholder="Rechercher par nom ou email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full h-14 pl-14 pr-6 rounded-2xl bg-slate-50 border border-slate-100 focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all outline-none font-medium text-sm"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-24">
                            <RefreshCw className="w-10 h-10 text-brand animate-spin mb-4" />
                            <p className="text-slate-500 font-bold">Chargement des administrateurs...</p>
                        </div>
                    ) : filteredUsers.length > 0 ? (
                        <table className="w-full">
                            <thead>
                                <tr className="bg-slate-50/50 text-left">
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-400 border-b border-slate-100 uppercase tracking-widest">Utilisateur</th>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-400 border-b border-slate-100 uppercase tracking-widest">Rôle</th>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-400 border-b border-slate-100 uppercase tracking-widest text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredUsers.map((usr) => (
                                    <tr key={usr.id} className="hover:bg-slate-50/50 transition-all group">
                                        <td className="px-8 py-5">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-2xl bg-brand/10 text-brand flex items-center justify-center font-black text-lg">
                                                    {(usr.full_name || 'A')[0].toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="font-extrabold text-green-950 text-sm flex items-center gap-2">
                                                        {usr.full_name}
                                                        {currentUser?.id === usr.id && (
                                                            <span className="px-2 py-0.5 bg-sky-100 text-sky-600 rounded-full text-[9px] uppercase tracking-wider">Vous</span>
                                                        )}
                                                    </p>
                                                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                                        <Mail className="w-3 h-3 text-slate-400" />
                                                        {usr.email}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-5">
                                            <div className="flex items-center gap-2">
                                                {usr.is_staff ? (
                                                    <div className="px-3 py-1 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
                                                        <Shield className="w-3 h-3" /> Admin
                                                    </div>
                                                ) : (
                                                    <div className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
                                                        <User className="w-3 h-3" /> Utilisateur
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-8 py-5 text-right">
                                            {/* Admin Users that are not superusers should not be able to delete */}
                                            {currentUser?.is_superuser && (
                                                showDeleteConfirm === usr.id ? (
                                                    <div className="flex items-center justify-end gap-2 animate-in fade-in slide-in-from-right-4">
                                                        <span className="text-xs font-bold text-red-500 mr-2">Confirmer ?</span>
                                                        <button
                                                            onClick={() => handleDeleteUser(usr.id)}
                                                            className="p-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all"
                                                        >
                                                            <Check className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => setShowDeleteConfirm(null)}
                                                            className="p-2 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition-all"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => setShowDeleteConfirm(usr.id)}
                                                        disabled={currentUser?.id === usr.id}
                                                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ml-auto ${currentUser?.id === usr.id
                                                            ? 'bg-slate-50 text-slate-300 cursor-not-allowed'
                                                            : 'bg-white text-slate-400 border border-slate-200 hover:bg-red-50 hover:text-red-500 hover:border-red-100 shadow-sm'
                                                            }`}
                                                        title={currentUser?.id === usr.id ? "Vous ne pouvez pas vous supprimer" : "Supprimer"}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="py-24 text-center px-4">
                            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-dashed border-slate-200">
                                <UserCheck className="w-8 h-8 text-slate-300" />
                            </div>
                            <h3 className="text-lg font-black text-green-950 mb-2">Aucun administrateur trouvé</h3>
                            <p className="text-slate-500 text-sm max-w-md mx-auto">
                                Vous pouvez ajouter de nouveaux membres à votre équipe en cliquant sur le bouton "Ajouter un admin".
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Add User Modal */}
            {isAddUserModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-green-950/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden animate-in slide-in-from-bottom-8 duration-300">
                        <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                            <div>
                                <h3 className="text-xl font-black text-green-950">Nouvel Administrateur</h3>
                                <p className="text-xs text-slate-500 font-medium mt-1">Créez un accès pour un nouveau membre de l'équipe</p>
                            </div>
                            <button
                                onClick={() => setIsAddUserModalOpen(false)}
                                className="w-10 h-10 rounded-2xl bg-white text-slate-400 flex items-center justify-center border border-slate-200 hover:bg-slate-100 transition-all shadow-sm"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-8 space-y-6">
                            <div className="space-y-4">
                                {/* Name */}
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                                        Nom Complet
                                    </label>
                                    <input
                                        type="text"
                                        name="full_name"
                                        value={formData.full_name}
                                        onChange={handleInputChange}
                                        placeholder="Ex: Jean Dupont"
                                        className={`w-full h-12 px-4 rounded-xl border text-sm focus:ring-2 focus:ring-brand/20 outline-none transition-all ${formErrors.full_name ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-brand bg-slate-50'
                                            }`}
                                    />
                                    {formErrors.full_name && <p className="text-red-500 text-xs mt-1 ml-1">{formErrors.full_name}</p>}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                                        Adresse Email
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="jean.dupont@example.com"
                                        className={`w-full h-12 px-4 rounded-xl border text-sm focus:ring-2 focus:ring-brand/20 outline-none transition-all ${formErrors.email ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-brand bg-slate-50'
                                            }`}
                                    />
                                    {formErrors.email && <p className="text-red-500 text-xs mt-1 ml-1">{formErrors.email}</p>}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    {/* Password */}
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                                            Mot de passe
                                        </label>
                                        <input
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            placeholder="••••••••"
                                            className={`w-full h-12 px-4 rounded-xl border text-sm focus:ring-2 focus:ring-brand/20 outline-none transition-all ${formErrors.password ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-brand bg-slate-50'
                                                }`}
                                        />
                                        {formErrors.password && <p className="text-red-500 text-xs mt-1 ml-1">{formErrors.password}</p>}
                                    </div>

                                    {/* Confirm Password */}
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                                            Confirmation
                                        </label>
                                        <input
                                            type="password"
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            placeholder="••••••••"
                                            className={`w-full h-12 px-4 rounded-xl border text-sm focus:ring-2 focus:ring-brand/20 outline-none transition-all ${formErrors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-slate-200 focus:border-brand bg-slate-50'
                                                }`}
                                        />
                                        {formErrors.confirmPassword && <p className="text-red-500 text-xs mt-1 ml-1">{formErrors.confirmPassword}</p>}
                                    </div>
                                </div>

                                {/* Permissions Info Box */}
                                <div className="p-4 bg-brand/5 border border-brand/10 rounded-2xl flex items-start gap-3 mt-4">
                                    <Shield className="w-5 h-5 text-brand shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-bold text-green-950">Accès Administrateur</p>
                                        <p className="text-xs text-slate-500 leading-relaxed mt-1">
                                            Cet utilisateur aura accès au tableau de bord, à la gestion des produits, des commandes et des autres utilisateurs.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-6 border-t border-slate-100 flex gap-3">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => setIsAddUserModalOpen(false)}
                                    className="flex-1 h-12"
                                    disabled={isSubmitting}
                                >
                                    Annuler
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="flex-1 h-12"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
                                    ) : (
                                        'Créer l\'accès'
                                    )}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default AdminUsers;
